import { isEqual } from 'lodash';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSettingsContext } from 'src/hooks/useSettings';
import { getNestedValue } from 'src/utils/settings';

/* ───────── helpers ───────── */
const generateGuid = () =>
  'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });

/* ────────────────────────────────────────────────────────────────
   HOOK: useTableViewManager
───────────────────────────────────────────────────────────────── */
export const useTableViewManager = (
  tableSettings: {
    tableName: string;
    apiRef?: any;
    columnVisibilityModel?: any;
  },
  { syncGrid = false } = {}
) => {
  const {
    settings,
    setSetting,
    setMultipleSettings,
    deleteSetting, // still available for external callers
  } = useSettingsContext();

  const { tableName, apiRef, columnVisibilityModel: defaultModel } =
    tableSettings;

  /* ───── constants ───── */
  const DEFAULT_VIEW_ID = '0';
  const DEFAULT_VIEW_NAME = 'Default';
  const DEFAULT_NEW_NAME = 'New View';

  /* ───── local state ───── */
  const [allTableViews, setAllTableViews] = useState<Record<string, any>>({});
  const [activeViewId, setActiveViewId] = useState(DEFAULT_VIEW_ID);
  const [activeModel, setActiveModel] = useState<Record<string, any>>({});
  const [savedModel, setSavedModel] = useState<Record<string, any>>({});
  const [activeViewName, setActiveViewName] = useState(DEFAULT_VIEW_NAME);
  const [savedViewName, setSavedViewName] = useState(DEFAULT_VIEW_NAME);
  const [isDirty, setIsDirty] = useState(false);

  /* ───── reflect Settings → Local State ───── */
  useEffect(() => {
    let views: Record<string, any> = getNestedValue(
      settings,
      ['TableColumnVisibility', tableName],
      {}
    );

    /* guarantee the Default view exists – write to Settings only once */
    if (!views[DEFAULT_VIEW_ID]) {
      views = {
        [DEFAULT_VIEW_ID]: {
          NameActive: DEFAULT_VIEW_NAME,
          NameSaved: DEFAULT_VIEW_NAME,
          Active: {},
          Saved: {},
        },
        ...views,
      };
      setSetting(['TableColumnVisibility', tableName], views);
    }

    const id = getNestedValue(
      settings,
      ['CurrentTableView', tableName],
      DEFAULT_VIEW_ID
    );

    const path = <
      K extends 'Active' | 'Saved' | 'NameActive' | 'NameSaved'
    >(
      k: K
    ) => ['TableColumnVisibility', tableName, id, k];

    const newActive = getNestedValue(settings, path('Active'), {});
    const newSaved = getNestedValue(settings, path('Saved'), {});
    const newActiveName = getNestedValue(
      settings,
      path('NameActive'),
      'New View'
    );
    const newSavedName = getNestedValue(
      settings,
      path('NameSaved'),
      'New View'
    );

    setAllTableViews(views);
    setActiveViewId(id);
    setActiveModel(newActive);
    setSavedModel(newSaved);
    setActiveViewName(newActiveName);
    setSavedViewName(newSavedName);

    const modelsDiffer = !isEqual(newActive, newSaved);
    const namesDiffer = newActiveName !== newSavedName;
    setIsDirty(modelsDiffer || namesDiffer);
  }, [settings, tableName, setSetting]);

  /* ───── grid synchronisation ───── */
  useEffect(() => {
    if (!syncGrid || !apiRef?.current) return;

    apiRef.current.setColumnVisibilityModel(
      Object.keys(activeModel).length ? activeModel : defaultModel
    );
  }, [syncGrid, apiRef, activeModel, defaultModel]);

  /* ───── derived list for dropdown ───── */
  const viewsList = useMemo(
    () =>
      Object.entries(allTableViews).map(([id, view]: any) => ({
        id,
        name:
          view.NameActive ??
          view.NameSaved ??
          (id === DEFAULT_VIEW_ID ? DEFAULT_VIEW_NAME : id),
      })),
    [allTableViews]
  );

  /* ───── mutating helpers (write to Settings) ───── */
  const handleViewChange = useCallback(
    (newId: string) =>
      setSetting(['CurrentTableView', tableName], newId),
    [setSetting, tableName]
  );

  /* ---- Saving: persist current active state ---- */
  const save = () =>
    setMultipleSettings([
      {
        key: ['TableColumnVisibility', tableName, activeViewId, 'Saved'],
        value: activeModel,
      },
      {
        key: ['TableColumnVisibility', tableName, activeViewId, 'NameSaved'],
        value: activeViewName.trim() || DEFAULT_VIEW_NAME,
      },
    ]);

  /* ---- Discard: revert active state back to saved ---- */
  const discard = () => {
    setMultipleSettings([
      {
        key: ['TableColumnVisibility', tableName, activeViewId, 'Active'],
        value: savedModel,
      },
      {
        key: ['TableColumnVisibility', tableName, activeViewId, 'NameActive'],
        value: savedViewName,
      },
    ]);

    if (syncGrid && apiRef?.current) {
      apiRef.current.setColumnVisibilityModel(savedModel);
    }
  };

  /* ---- Create a brand-new view ---- */
  const addView = (rawName: string = DEFAULT_NEW_NAME) => {
    const name = rawName.trim() || DEFAULT_NEW_NAME;
    const newId = generateGuid();

    setMultipleSettings([
      {
        key: ['TableColumnVisibility', tableName],
        value: {
          ...allTableViews,
          [newId]: {
            NameActive: name,
            NameSaved: name,
            Active: activeModel,
            Saved: activeModel,
          },
        },
      },
      {
        key: ['CurrentTableView', tableName],
        value: newId,
      },
    ]);
  };

  /* ---- Rename: change only the ACTIVE name ---- */
  const renameView = (viewId: string, newName: string) => {
    if (viewId === DEFAULT_VIEW_ID) return;
    setSetting(
      ['TableColumnVisibility', tableName, viewId, 'NameActive'],
      newName.trim() || DEFAULT_VIEW_NAME
    );
  };

  /* ---- Delete: now atomic with setMultipleSettings -------------- */
  const deleteView = (viewId: string) => {
    if (viewId === DEFAULT_VIEW_ID) return;
    setMultipleSettings([
      {
        key: ['TableColumnVisibility', tableName, viewId],
        delete: true,
      },
      {
        key: ['CurrentTableView', tableName],
        value: DEFAULT_VIEW_ID,
      },
    ]);
  };

  /* ───── hook API ───── */
  return {
    activeViewId,
    activeViewName,
    viewsList,
    handleViewChange,
    isDirty,
    save,
    discard,
    addView,
    renameView,
    deleteView,
  };
};
